<template>
  <div class="freight-client-select">
    <label class="freight-client-select__label">
      <span class="freight-client-select__required">*</span>
      Cliente
    </label>
    <!-- maxTagCount: a caixa mostra um cliente e "+N", sem crescer; a lista completa
         fica no dropdown, com os selecionados marcados. O "+N" vai como texto: o a-select
         chama uma função passada aqui com o `h` do Vue, não com os valores omitidos. -->
    <a-select
      mode="multiple"
      size="large"
      show-search
      allow-clear
      label-in-value
      option-label-prop="label"
      dropdown-class-name="freight-client-select__dropdown"
      :value="selectValue"
      :max-tag-count="1"
      :auto-clear-search-value="false"
      :max-tag-placeholder="`+${selected.length - 1}`"
      :filter-option="false"
      :not-found-content="fetching ? undefined : null"
      placeholder="Informe o cliente"
      class="freight-client-select__select"
      @search="onSearch"
      @change="onChange"
    >
      <a-spin v-if="fetching" slot="notFoundContent" size="small" />
      <a-select-option
        v-for="option in dropdownOptions"
        :key="option.value"
        :value="option.value"
        :label="tagLabel(option.label, option.documento)"
      >
        <div class="freight-client-select__option">
          <a-checkbox :checked="isSelected(option.value)" />
          <div class="freight-client-select__option-text">
            <strong>{{ option.label }}</strong>
            <span v-if="option.documento">{{ option.documento }}</span>
          </div>
        </div>
      </a-select-option>
    </a-select>
    <p v-if="emptyMessage && !fetching" class="freight-client-select__empty">
      {{ emptyMessage }}
    </p>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import FreightTablesApi from "@/modules/FreightTables/services/FreightTablesApi";
import { formatCNPJFromParts } from "@/utils";
import { handleApiError } from "@/shared/utils/handleApiError";

const MIN_SEARCH_LENGTH = 3;

// A busca não filtra por papel porque `pessoa` não tem flag de cliente — cliente/tomador é
// papel na tabela de frete, não tipo de pessoa.
function pessoaJuridicaOption(item) {
  return {
    value: item.pessoa_id,
    label: item.razao_social || "",
    documento: formatCNPJFromParts(
      item.cnpj_base,
      item.cnpj_filial,
      item.cnpj_digito,
    ),
  };
}

function pessoaFisicaOption(item) {
  return {
    value: item.pessoa_id,
    label: item.nome || "",
    documento: item.cpf || "",
  };
}

function pessoaExteriorOption(item) {
  return {
    value: item.pessoa_id,
    label: item.nome || "",
    documento: item.cuit || "",
  };
}

function collectOptions(payload) {
  const data = payload || {};
  return [
    (data.items || []).map(pessoaJuridicaOption),
    (data.itemsF || []).map(pessoaFisicaOption),
    (data.itemsE || []).map(pessoaExteriorOption),
  ]
    .flat()
    .filter((option) => option.value != null);
}

function sameId(a, b) {
  return String(a) === String(b);
}

// label-in-value pode devolver "nome\ndocumento" da tag; o nome é só a 1ª linha.
function readOptionLabel(label) {
  if (label == null) return "";
  let text = "";
  if (typeof label === "string" || typeof label === "number") {
    text = String(label);
  } else if (label.text) {
    text = String(label.text);
  }
  return text.split("\n")[0];
}

export default {
  name: "FreightTableClientSelect",
  props: {
    // Array de { pessoa_id, nome, documento } (.sync com o formulário pai).
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      fetching: false,
      options: [],
      emptyMessage: "",
      requestSeq: 0,
    };
  },
  computed: {
    selected() {
      return Array.isArray(this.value) ? this.value : [];
    },
    selectValue() {
      return this.selected.map((client) => ({
        key: client.pessoa_id,
        label: this.tagLabel(client.nome || String(client.pessoa_id), client.documento),
      }));
    },
    // Selecionados primeiro, depois o resultado da busca que ainda não está na seleção.
    dropdownOptions() {
      const selected = this.selected.map((client) => ({
        value: client.pessoa_id,
        label: client.nome || String(client.pessoa_id),
        documento: client.documento || "",
      }));
      return selected.concat(
        this.options.filter((option) => !this.isSelected(option.value)),
      );
    },
  },
  created() {
    this.debouncedFetch = debounce(this.fetchClients, 500);
  },
  beforeDestroy() {
    if (this.debouncedFetch) {
      this.debouncedFetch.cancel();
    }
  },
  methods: {
    // Tag em duas linhas, como a da transportadora: nome e, embaixo, o documento. Vai também
    // no `label` da opção, porque o a-select desenha a tag com o label da opção quando ela
    // está na lista — e os selecionados ficam na lista, marcados.
    tagLabel(nome, documento) {
      return documento ? `${nome}\n${documento}` : nome;
    },
    isSelected(id) {
      return this.selected.some((client) => sameId(client.pessoa_id, id));
    },
    onSearch(term) {
      this.emptyMessage = "";
      this.debouncedFetch(term);
    },
    onChange(selected) {
      const items = Array.isArray(selected) ? selected : [];
      const clients = items.map((item) => {
        const fromSearch = this.options.find((option) =>
          sameId(option.value, item.key),
        );
        const previous = this.selected.find((client) =>
          sameId(client.pessoa_id, item.key),
        );
        return {
          pessoa_id: item.key,
          nome:
            (fromSearch && fromSearch.label) ||
            (previous && previous.nome) ||
            readOptionLabel(item.label),
          documento:
            (fromSearch && fromSearch.documento) ||
            (previous && previous.documento) ||
            "",
        };
      });

      // O resultado da busca fica na lista: dá para marcar vários clientes de uma vez.
      this.emptyMessage = "";
      this.$emit("update:value", clients);
    },
    fetchClients(term) {
      const search = String(term || "").trim();
      if (search.length < MIN_SEARCH_LENGTH) {
        this.options = [];
        this.fetching = false;
        this.emptyMessage = "";
        return;
      }

      const seq = ++this.requestSeq;
      this.fetching = true;
      this.emptyMessage = "";

      FreightTablesApi.listClients(search)
        .then((response) => {
          if (seq !== this.requestSeq) return;
          this.options = collectOptions((response && response.data) || {});
          this.emptyMessage = this.options.length
            ? ""
            : "Nenhum cliente encontrado para a busca informada.";
        })
        .catch((error) => {
          if (seq !== this.requestSeq) return;
          this.options = [];
          this.emptyMessage = "";
          handleApiError(this, error, "Não foi possível buscar os clientes");
        })
        .then(() => {
          if (seq !== this.requestSeq) return;
          this.fetching = false;
        });
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.freight-client-select {
  font-family: @font-primary;

  &__label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.88);
  }

  &__required {
    color: @color-danger-action;
  }

  &__select {
    width: 100%;
  }

  &__empty {
    margin: 4px 0 0;
    font-size: 12px;
    color: @color-text-light;
  }

  // Caixa e tag no mesmo desenho da Transportadora, que fica ao lado.
  /deep/ .ant-select-selection {
    border-radius: 8px;
    min-height: 40px;
    padding: 4px 8px;
  }

  /deep/ .ant-select-selection__rendered {
    margin-left: 4px;
    line-height: 30px;
  }

  // Flex no lugar do float do antd: a tag "+N" estica até a altura da tag do cliente,
  // tenha ela uma linha (sem documento) ou duas. O `::after` do antd só segurava a altura da
  // caixa com os itens flutuando; com o flex ele cairia numa segunda linha vazia.
  /deep/ .ant-select-selection__rendered > ul {
    display: flex;
    flex-wrap: wrap;
  }

  // Sem float, o `margin-bottom: -3px` do antd passa a encolher a caixa; zerado, a altura
  // bate com a da Transportadora ao lado, vazia ou com tag.
  /deep/ .ant-select-selection__rendered {
    margin-bottom: 0;

    &::after {
      display: none;
    }
  }

  /deep/ .ant-select-selection--multiple .ant-select-selection__choice {
    border-radius: 6px;
    background: @color-bg-info;
    border-color: transparent;
    color: @color-text-dark;
    height: auto;
    max-width: calc(100% - 64px);
    margin-top: 4px;
    padding: 4px 24px 4px 8px;
    line-height: 1.3;
  }

  // `pre` mantém as duas linhas sem deixar o nome longo quebrar numa terceira: cada linha
  // corta com reticências e a caixa não cresce.
  /deep/ .ant-select-selection--multiple .ant-select-selection__choice__content {
    display: block;
    white-space: pre;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 14px;
    line-height: 18px;
  }

  /deep/ .ant-select-selection--multiple .ant-select-selection__choice__disabled {
    display: flex;
    align-items: center;
    padding-right: 8px;
  }

  /deep/ .ant-select-selection--multiple .ant-select-selection__choice__remove {
    top: 50%;
    transform: translateY(-50%);
    right: 6px;
  }
}
</style>

<!-- Dropdown é renderizado no body: estilos sem scoped. -->
<style lang="less">
@import "~@/shared/styles/variables.less";

.freight-client-select__dropdown {
  font-family: @font-primary;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);

  .ant-select-dropdown-menu {
    max-height: 320px;
  }

  .ant-select-dropdown-menu-item {
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }
  }

  // O checkbox da opção já indica a seleção; o ícone padrão do antd ficaria duplicado.
  // Seletor mais longo de propósito: precisa vencer a regra do antd para item selecionado com
  // hover (`…-item-selected:hover .ant-select-selected-icon`), que mostra o ícone de novo.
  &.ant-select-dropdown.ant-select-dropdown--multiple
    .ant-select-dropdown-menu
    .ant-select-dropdown-menu-item
    .ant-select-selected-icon {
    display: none;
  }

  .freight-client-select__option {
    display: flex;
    align-items: center;
    gap: 12px;

    // O clique vai para a opção; o checkbox só espelha o estado.
    .ant-checkbox-wrapper {
      pointer-events: none;
    }
  }

  .freight-client-select__option-text {
    display: flex;
    flex-direction: column;
    min-width: 0;

    strong {
      font-size: 14px;
      font-weight: 400;
      line-height: 22px;
      color: rgba(0, 0, 0, 0.88);
    }

    span {
      font-size: 12px;
      line-height: 20px;
      color: rgba(0, 0, 0, 0.45);
    }
  }
}
</style>
